<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-gray-900">SMS Templates</h2>
        <p class="text-sm text-gray-500 mt-0.5">Create reusable message templates with shortcodes</p>
      </div>
      <button @click="openModal()"
        class="inline-flex items-center px-4 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors"
        style="height:40px">
        <i class="fa-solid fa-plus mr-1.5"></i>New Template
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
      <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mb-3"></i>
      <p class="text-sm text-gray-400">Loading templates...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="templates.length === 0" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
      <i class="fa-solid fa-file-lines text-gray-300 text-3xl mb-3"></i>
      <p class="text-sm text-gray-500 font-medium">No templates yet</p>
      <p class="text-xs text-gray-400 mt-1">Create your first SMS template to get started</p>
      <button @click="openModal()"
        class="mt-4 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors">
        <i class="fa-solid fa-plus mr-1.5"></i>Create Template
      </button>
    </div>

    <!-- Templates table -->
    <div v-else class="bg-white rounded-xl border border-gray-200">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th class="py-3 px-4">Name</th>
              <th class="py-3 px-4">Type</th>
              <th v-if="customFieldsEnabled" class="py-3 px-4">Service</th>
              <th class="py-3 px-4">Body</th>
              <th class="py-3 px-4 text-center w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in templates" :key="t.id" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td class="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">{{ t.name }}</td>
              <td class="py-3 px-4">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="typeBadgeClass(t.type)">
                  {{ typeLabel(t.type) }}
                </span>
              </td>
              <td v-if="customFieldsEnabled" class="py-3 px-4 text-gray-600 whitespace-nowrap">{{ serviceLabel(t.service) }}</td>
              <td class="py-3 px-4 text-gray-600 truncate" style="max-width:300px">{{ t.body }}</td>
              <td class="py-3 px-4 text-center">
                <div class="flex items-center justify-center gap-1">
                  <button @click="openModal(t)"
                    class="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="Edit">
                    <i class="fa-solid fa-pen text-xs"></i>
                  </button>
                  <button @click="confirmDelete(t)"
                    class="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete">
                    <i class="fa-solid fa-trash text-xs"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">
            {{ editingTemplate ? 'Edit Template' : 'New Template' }}
          </h3>
        </div>
        <div class="px-6 py-5 space-y-4">
          <!-- Name -->
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block text-left">Name</label>
            <input v-model="form.name" type="text" placeholder="e.g. Weekly Check-In"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors" />
          </div>

          <!-- Type -->
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block text-left">Type</label>
            <div class="flex gap-2">
              <button v-for="opt in typeOptions" :key="opt.value" @click="form.type = opt.value"
                class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
                :class="form.type === opt.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Service -->
          <div v-if="customFieldsEnabled">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block text-left">Service</label>
            <select v-model="form.service"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors">
              <option value="all">All services</option>
              <option v-for="s in services" :key="s.key" :value="s.key">{{ s.label }}</option>
            </select>
            <p class="text-xs text-gray-400 mt-1 text-left">
              A template locked to a service only appears when sending for a job of that service, and never on bulk sends.
            </p>
          </div>

          <!-- Body -->
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block text-left">Message Body</label>
            <textarea ref="bodyTextarea" v-model="form.body" rows="5" placeholder="Hi {recipientName} - ..."
              class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
            <div class="mt-2">
              <p class="text-xs text-gray-400 mb-1.5 text-left">Shortcodes — click to insert:</p>
              <div class="flex flex-wrap gap-1.5">
                <button v-for="sc in staticShortcodes" :key="sc.code" @click="insertShortcode(sc.code)"
                  class="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors cursor-pointer">
                  <code class="mr-1">{{ sc.code }}</code>
                  <span class="text-indigo-400 font-normal">{{ sc.label }}</span>
                </button>
              </div>

              <!-- Per-service field shortcodes. Hidden for a service-locked
                   template unless the group matches, so the picker can't offer
                   a token that will always resolve blank. -->
              <div v-for="g in visibleShortcodeGroups" :key="g.key" class="mt-3">
                <p class="text-xs text-gray-400 mb-1.5 text-left">{{ g.label }}</p>
                <div class="flex flex-wrap gap-1.5">
                  <button v-for="sc in g.codes" :key="sc.code" @click="insertShortcode(sc.code)"
                    class="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors cursor-pointer">
                    <code class="mr-1">{{ sc.code }}</code>
                    <span class="text-emerald-400 font-normal">{{ sc.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Live preview -->
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block text-left">Preview</label>
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap break-words" style="min-height:60px">{{ previewText }}</div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="showModal = false"
            class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button @click="saveTemplate()" :disabled="saving || !form.name.trim() || !form.body.trim()"
            class="px-5 h-10 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors disabled:opacity-50">
            <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ saving ? 'Saving...' : (editingTemplate ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div v-if="deleteTarget" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">Delete Template</h3>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-600">Are you sure you want to delete <span class="font-semibold">{{ deleteTarget.name }}</span>?</p>
          <p class="text-xs text-gray-400 mt-2">This action cannot be undone.</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="deleteTarget = null"
            class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button @click="deleteTemplate()" :disabled="deleting"
            class="px-5 h-10 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50">
            <i v-if="deleting" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { loadDefinitions, activeServices, smsShortcodeGroups } from "../../services/customFields";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      templates: [],
      showModal: false,
      editingTemplate: null,
      deleteTarget: null,
      customFieldDefs: null,
      form: {
        name: '',
        body: '',
        type: 'all',
        service: 'all',
      },
      typeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Bulk', value: 'bulk' },
        { label: 'Single Job', value: 'single' },
        { label: 'Client Book', value: 'clientBook' },
      ],
      shortcodes: [
        { code: '{recipientName}', label: 'Recipient name' },
        { code: '{company}', label: 'Company' },
        { code: '{project}', label: 'Project name' },
        { code: '{contact}', label: 'Primary contact' },
        { code: '{address}', label: 'Job address' },
        { code: '{date}', label: 'Job date' },
        { code: '{time}', label: 'Job time' },
        { code: '{meters}', label: 'Meters' },
        { code: '{link}', label: 'Client portal link' },
      ],
    };
  },
  computed: {
    customFieldsEnabled() {
      return !!(this.customFieldDefs && this.customFieldDefs.enabled);
    },
    services() {
      return this.customFieldsEnabled ? activeServices(this.customFieldDefs) : [];
    },
    staticShortcodes() {
      if (!this.customFieldsEnabled) return this.shortcodes;
      return this.shortcodes.concat([
        { code: '{jobDetails}', label: "All of the job's details" },
      ]);
    },
    shortcodeGroups() {
      return this.customFieldsEnabled ? smsShortcodeGroups(this.customFieldDefs) : [];
    },
    visibleShortcodeGroups() {
      if (this.form.service === 'all') return this.shortcodeGroups;
      return this.shortcodeGroups.filter((g) => g.key === this.form.service);
    },
    previewText() {
      let out = this.form.body
        .replace(/\{recipientName\}/g, 'John Smith')
        .replace(/\{company\}/g, 'Acme Corp')
        .replace(/\{project\}/g, 'Site Inspection')
        .replace(/\{contact\}/g, 'Jane Doe')
        .replace(/\{address\}/g, '123 Example St, Brisbane QLD 4000')
        .replace(/\{date\}/g, '15/07/2026')
        .replace(/\{time\}/g, '9:00AM')
        .replace(/\{meters\}/g, '50')
        .replace(/\{link\}/g, 'https://app.kct.net.au/job/abc123');
      // Sample values so the admin can see the shape of the message. Real sends
      // blank any token the job's service does not define.
      this.shortcodeGroups.forEach((g) => {
        g.codes.forEach((sc) => {
          out = out.split(sc.code).join(`[${sc.label}]`);
        });
      });
      return out.replace(/\{jobDetails\}/g, 'Core Diameter (mm): 100, Number of Cores: 4');
    },
  },
  methods: {
    serviceLabel(key) {
      if (!key || key === 'all') return 'All services';
      const s = this.services.find((x) => x.key === key);
      return s ? s.label : key;
    },
    typeBadgeClass(type) {
      const map = {
        all: 'bg-gray-100 text-gray-700',
        bulk: 'bg-indigo-100 text-indigo-700',
        single: 'bg-green-100 text-green-700',
        clientBook: 'bg-amber-100 text-amber-700',
      };
      return map[type] || map.all;
    },
    typeLabel(type) {
      const map = { all: 'All', bulk: 'Bulk', single: 'Single Job', clientBook: 'Client Book' };
      return map[type] || type;
    },
    getCurrentUser() {
      return new Promise((resolve, reject) => {
        if (auth.currentUser) return resolve(auth.currentUser);
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          user ? resolve(user) : reject(new Error('Not authenticated'));
        });
      });
    },
    async getHeaders() {
      const user = await this.getCurrentUser();
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    },
    async fetchTemplates() {
      this.loading = true;
      try {
        const headers = await this.getHeaders();
        const res = await fetch(`${API_BASE}/sms/templates`, { headers });
        const json = await res.json();
        this.templates = json.data || [];
      } catch (e) {
        console.error('Failed to load templates:', e);
      }
      this.loading = false;
    },
    openModal(template) {
      if (template) {
        this.editingTemplate = template;
        // Templates created before service scoping have no `service`.
        this.form = { name: template.name, body: template.body, type: template.type || 'all', service: template.service || 'all' };
      } else {
        this.editingTemplate = null;
        this.form = { name: '', body: '', type: 'all', service: 'all' };
      }
      this.showModal = true;
    },
    insertShortcode(code) {
      const textarea = this.$refs.bodyTextarea;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = this.form.body.substring(0, start);
        const after = this.form.body.substring(end);
        this.form.body = before + code + after;
        this.$nextTick(() => {
          textarea.focus();
          textarea.setSelectionRange(start + code.length, start + code.length);
        });
      } else {
        this.form.body += code;
      }
    },
    async saveTemplate() {
      if (!this.form.name.trim() || !this.form.body.trim()) return;
      this.saving = true;
      try {
        const headers = await this.getHeaders();
        if (this.editingTemplate) {
          await fetch(`${API_BASE}/sms/templates/${this.editingTemplate.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(this.form),
          });
        } else {
          await fetch(`${API_BASE}/sms/templates`, {
            method: 'POST',
            headers,
            body: JSON.stringify(this.form),
          });
        }
        this.showModal = false;
        await this.fetchTemplates();
      } catch (e) {
        console.error('Failed to save template:', e);
      }
      this.saving = false;
    },
    confirmDelete(template) {
      this.deleteTarget = template;
    },
    async deleteTemplate() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        const headers = await this.getHeaders();
        await fetch(`${API_BASE}/sms/templates/${this.deleteTarget.id}`, {
          method: 'DELETE',
          headers,
        });
        this.deleteTarget = null;
        await this.fetchTemplates();
      } catch (e) {
        console.error('Failed to delete template:', e);
      }
      this.deleting = false;
    },
  },
  created() {
    this.fetchTemplates();
    loadDefinitions().then((defs) => { this.customFieldDefs = defs; });
  },
};
</script>
